import React, { useState, useMemo, useCallback, useRef } from "react";
import cn from "classnames";
import Select from "react-select";
import { useField, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { useHttp } from "hooks/http.hook";
import Label from "components/InputsField/Label";
import InputError from "components/InputsField/InputError";
import InputNote from "components/InputsField/InputNote";

import styles from "./styles.module.scss";

const EmployeeAutoComplete = ({
    url,
    name,
    label,
    required,
    className,
    selectClassName,
    errorMode,
    note,
    noOptionsMessage,
    ...props
}) => {
    const { t } = useTranslation();
    const { request } = useHttp();
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const [field, meta] = useField({ name });
    const [isLoading, setLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const hasLoadedRef = useRef(false);

    const loadEmployees = useCallback(async () => {
        if (hasLoadedRef.current || isLoading) return;

        setLoading(true);
        try {
            const result = await request(`${url}?q=`);
            const employees = result.data?.items || result.data || [];
            if (employees.length) {
                const formattedOptions = employees.map((item) => ({
                    value: item.id,
                    label: `${item.firstname} ${item.lastname} ${
                        item.midlename ? item.midlename : ""
                    }`.trim(),
                }));
                setOptions(formattedOptions);
                hasLoadedRef.current = true;
            }
        } catch (error) {
            console.error("Error loading employees:", error);
        } finally {
            setLoading(false);
        }
    }, [url, request, isLoading]);

    const handleMenuOpen = () => {
        if (!hasLoadedRef.current) {
            loadEmployees();
        }
    };

    const handleChange = (selectedOptions) => {
        setFieldValue(name, selectedOptions);
    };

    const handleBlur = useCallback(() => {
        setFieldTouched(name);
    }, [name, setFieldTouched]);

    const formattedValue = useMemo(() => {
        if (field.value) {
            const employees = field.value.map((item) => {
                if (item.label) {
                    return item;
                } else {
                    return {
                        value: item.id,
                        label: `${item.firstname} ${item.lastname} ${
                            item.midlename ? item.midlename : ""
                        }`.trim(),
                    };
                }
            });
            return employees;
        }
        return null;
    }, [field]);

    const filterOption = (option, inputValue) => {
        if (!inputValue) return true;
        return option.label.toLowerCase().includes(inputValue.toLowerCase());
    };

    return (
        <div className={cn(className, styles.wrapper)}>
            <Label name={name} label={label} required={required} className={styles.label} />
            <Select
                options={options}
                isLoading={isLoading}
                onMenuOpen={handleMenuOpen}
                onBlur={handleBlur}
                value={formattedValue}
                loadingMessage={() => t("loading")}
                noOptionsMessage={() => noOptionsMessage || t("addContact.noEmployee")}
                placeholder={t("addContact.employee")}
                classNamePrefix="select"
                onChange={handleChange}
                filterOption={filterOption}
                isClearable
                {...props}
                className={cn(styles.select, selectClassName, {
                    [styles["select--error"]]: meta.touched && meta.error,
                })}
            />
            {meta.touched && meta.error && (
                <InputError message={meta.error} mode={errorMode || "top-right"} />
            )}
            {note && (
                <div className={styles.note}>
                    <InputNote note={note} />
                </div>
            )}
        </div>
    );
};

export default EmployeeAutoComplete;
